import type { BoardFloorPlanData } from '../../../types';
import { contentBounds, fitPlanViewBox, formatInchesFtIn, type FloorPlanViewBox } from './floorPlanMath';

export type FloorPlanPdfPaper = 'letter' | 'tabloid';

export const FLOOR_PLAN_PDF_PAPER: Record<FloorPlanPdfPaper, { longIn: number; shortIn: number; label: string }> = {
  letter: { longIn: 11, shortIn: 8.5, label: 'Letter (8.5 × 11)' },
  tabloid: { longIn: 17, shortIn: 11, label: 'Tabloid (11 × 17)' },
};

export const FLOOR_PLAN_PDF_DPI = 150;
export const FLOOR_PLAN_PDF_MARGIN_IN = 0.4;
export const FLOOR_PLAN_PDF_TITLE_IN = 0.58;

export interface FloorPlanPdfLayout {
  paper: FloorPlanPdfPaper;
  orientation: 'landscape' | 'portrait';
  pageW: number;
  pageH: number;
  margin: number;
  titleH: number;
  drawW: number;
  drawH: number;
  viewBox: FloorPlanViewBox;
  scaleLabel: string;
  filename: string;
  widthPx: number;
  heightPx: number;
  titlePx: number;
  pxPerIn: number;
}

export function floorPlanPdfFilename(title?: string): string {
  const slug =
    (title || 'floor-plan')
      .trim()
      .replace(/[^\w]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'floor-plan';
  return `LCTF-${slug}.pdf`;
}

export function floorPlanScaleLabel(worldIn: number, paperIn: number): string {
  if (!(worldIn > 0) || !(paperIn > 0)) return 'Fitted';
  return `1" = ${formatInchesFtIn(worldIn / paperIn)}`;
}

export function floorPlanPdfLayout(data: BoardFloorPlanData, paper: FloorPlanPdfPaper): FloorPlanPdfLayout {
  const stock = FLOOR_PLAN_PDF_PAPER[paper];
  const bounds = contentBounds({ walls: data.walls, items: data.items, underlays: [] });
  const planW = bounds?.widthIn || data.footprint.widthIn;
  const planD = bounds?.depthIn || data.footprint.depthIn;
  const orientation: 'landscape' | 'portrait' = planW >= planD ? 'landscape' : 'portrait';
  const pageW = orientation === 'landscape' ? stock.longIn : stock.shortIn;
  const pageH = orientation === 'landscape' ? stock.shortIn : stock.longIn;
  const margin = FLOOR_PLAN_PDF_MARGIN_IN;
  const titleH = FLOOR_PLAN_PDF_TITLE_IN;
  const drawW = Math.max(pageW - margin * 2, 1);
  const drawH = Math.max(pageH - margin * 2 - titleH, 1);
  const viewBox = fitPlanViewBox({ ...data, underlays: [] }, drawW * 100, drawH * 100, 28);
  const targetTypeIn = 10 / 72;
  const fontPlanIn = targetTypeIn * (viewBox.w / drawW);
  const pxPerIn = fontPlanIn > 0 ? 12 / fontPlanIn : 8;
  return {
    paper,
    orientation,
    pageW,
    pageH,
    margin,
    titleH,
    drawW,
    drawH,
    viewBox,
    scaleLabel: floorPlanScaleLabel(viewBox.w, drawW),
    filename: floorPlanPdfFilename(data.title),
    widthPx: Math.round(pageW * FLOOR_PLAN_PDF_DPI),
    heightPx: Math.round(pageH * FLOOR_PLAN_PDF_DPI),
    titlePx: Math.round(titleH * FLOOR_PLAN_PDF_DPI),
    pxPerIn,
  };
}

export function floorPlanPdfDate(now = new Date()): string {
  return now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
